/**
 * GlobalChatSystem Constants
 *
 * Centralized constants for the chat system.
 */

/**
 * Warning balance threshold in USD.
 * When user's OLV balance is worth less than this amount,
 * a warning banner is shown but user can still run tasks.
 * Calculated in real-time from OLV balance * current OLV rate.
 */
export const WARNING_BALANCE_THRESHOLD_USD = 10.00;

/**
 * Critical/blocking balance threshold in USD.
 * When the user's balance is AT OR BELOW this amount, the chat input is
 * blocked (in production mode only); in testing mode, only a warning shows.
 *
 * 2026-08-14: aligned with the server's hard balance stop — work pauses at
 * EMPTY ($0), not at the legacy $3 buffer. Only useLowBalanceWarning consumes
 * this constant (the comparison there is `<=`).
 */
export const CRITICAL_BALANCE_THRESHOLD_USD = 0.00;

/**
 * @deprecated Use CRITICAL_BALANCE_THRESHOLD_USD instead.
 * Kept for backward compatibility.
 */
export const LOW_BALANCE_THRESHOLD_USD = CRITICAL_BALANCE_THRESHOLD_USD;

/**
 * How long the touch message-stepper stays awake after the last scroll or
 * step before fading back out (2026-08 reading-comfort round).
 *
 * Long enough to reach for the control you just saw appear; short enough that
 * a thread you've stopped scrolling is never left with buttons sitting over
 * the text — the failure mode that got the phone pagers removed on
 * 2026-08-14.
 */
export const NAV_CLUSTER_SLEEP_MS = 2400;

/**
 * What wakes the touch message-stepper (2026-10-16): the person's own gestures on the transcript. Never `scroll`,
 * which the chat fires itself whenever it follows a reply down, and woke the steps over the reply's words.
 */
export const NAV_WAKE_GESTURES = ['touchmove', 'wheel'] as const;

/**
 * How long the transcript's fade outlasts a control at its foot (2026-10-16, useLingeringFlag): at least the longest
 * exit of the controls it lies under, the latest pill's (FloatingPillButton, 0.25s) and the touch stepper's
 * (ThreadNavCluster, 0.22s), so neither leaves over words a person can read. Held to both by threadScrollingUx.test.tsx.
 */
export const FOOT_CONTROL_EXIT_MS = 260;

/**
 * The transcript's fade under the latest pill (2026-10-16, styles.ts ChatMessages `data-fade-bottom`), measured from
 * the transcript's bottom edge: clear below `clearBelowPx`, full strength above `opaqueAbovePx`. The pill stands 12px
 * up and is at most 44px tall, so its middle falls in the clear band and its top edge where words keep at most a third
 * of their strength.
 */
export const TRANSCRIPT_FADE = { clearBelowPx: 36, opaqueAbovePx: 96 } as const;
